import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { renderTag } from "@/components/quickwash/TagRenderer";
import { Save, RefreshCw, Printer, Bold, AlignLeft, AlignCenter, AlignRight, Underline, ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";

const SAMPLE = {
  brand: "QUICK WASH",
  order: "QW1024",
  name: "Rahul Sharma",
  mobile: "9876543210",
  service: "Wash & Iron",
  item: "SHIRT",
  piece: "1 / 25",
  delivery: "12 Feb 2026",
  footer: "",
};

const AlignBtn = ({ active, onClick, children, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid} className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>{children}</button>
);

const TagLayoutEditor = () => {
  const [layout, setLayout] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings/tag-layout");
    setLayout(data.layout);
    setDefaults(data.defaults);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);

  if (!layout) return <div className="h-40 bg-slate-100 rounded-xl animate-pulse"/>;

  const setL = (patch) => { setLayout((l) => ({ ...l, ...patch })); setDirty(true); };
  const updateField = (i, patch) => {
    const fields = [...layout.fields];
    fields[i] = { ...fields[i], ...patch };
    setL({ fields });
  };
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= layout.fields.length) return;
    const fields = [...layout.fields];
    [fields[i], fields[j]] = [fields[j], fields[i]];
    setL({ fields });
  };
  const removeField = (i) => {
    const f = layout.fields[i];
    if (!window.confirm(`Remove '${f.key}' from the tag?`)) return;
    const fields = layout.fields.filter((_, idx) => idx !== i);
    setL({ fields });
    toast.success(`Removed '${f.key}'`);
  };
  const addCustomField = () => {
    const n = layout.fields.filter((f) => f.key.startsWith("custom_")).length + 1;
    const key = `custom_${n}`;
    const fields = [...layout.fields, {
      key,
      label: "Custom",
      value: "New line",
      enabled: true,
      bold: false,
      size: 10,
      align: "center",
      underline: false,
      isCustom: true,
    }];
    setL({ fields });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/tag-layout", { layout }); toast.success("Tag layout saved"); setDirty(false); }
    catch { toast.error("Could not save layout"); }
    finally { setSaving(false); }
  };
  const reset = () => { setLayout(defaults); setDirty(true); toast.info("Reset to defaults — click Save to apply"); };

  // Tweak sample piece string with current totals for a nice preview
  const previewData = { ...SAMPLE, brand: layout.brand_text || "QUICK WASH", footer: layout.footer_text || "" };

  const doPrint = () => window.print();

  return (
    <div className="space-y-6" data-testid="tag-layout-page">
      <div className="flex flex-wrap justify-between items-start gap-3 no-print">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>Custom Tag Editor</h1>
          <p className="text-sm text-slate-500">Design the exact TSC label your printer will use. Live preview on the right.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="qw-btn-ghost" data-testid="tag-reset"><RefreshCw size={16}/>Reset</button>
          <button onClick={doPrint} className="qw-btn-outline" data-testid="tag-testprint"><Printer size={16}/>Test Print</button>
          <button onClick={save} disabled={saving || !dirty} className="qw-btn-primary disabled:opacity-40" data-testid="tag-save"><Save size={16}/>{saving ? "Saving…" : dirty ? "Save Layout" : "All Saved"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT: Controls */}
        <div className="space-y-4 no-print">
          <div className="qw-dash-card p-6">
            <div className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }}>Tag Dimensions</div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="qw-label">Width (mm)</label><input type="number" min={30} max={200} className="qw-input" value={layout.width_mm} onChange={(e) => setL({ width_mm: Number(e.target.value) })} data-testid="tag-width"/></div>
              <div><label className="qw-label">Height (mm)</label><input type="number" min={20} max={200} className="qw-input" value={layout.height_mm} onChange={(e) => setL({ height_mm: Number(e.target.value) })} data-testid="tag-height"/></div>
              <div><label className="qw-label">Gap (mm)</label><input type="number" min={0} max={10} className="qw-input" value={layout.gap_mm} onChange={(e) => setL({ gap_mm: Number(e.target.value) })} data-testid="tag-gap"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="qw-label">Brand Text</label><input className="qw-input" value={layout.brand_text} onChange={(e) => setL({ brand_text: e.target.value })} data-testid="tag-brand-text"/></div>
              <div><label className="qw-label">Footer Text (optional)</label><input className="qw-input" value={layout.footer_text} onChange={(e) => setL({ footer_text: e.target.value })} placeholder="e.g. Thank you!" data-testid="tag-footer-text"/></div>
            </div>
          </div>

          <div className="qw-dash-card p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins' }}>Fields & Order</div>
              <button onClick={addCustomField} className="qw-btn-outline text-xs py-1.5 px-3" data-testid="tag-add-custom"><Plus size={12}/>Add Custom Text</button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Toggle visibility, edit, reorder, or remove each field. Add your own custom text lines (address, promotions, etc.).</p>
            <div className="space-y-2">
              {layout.fields.map((f, i) => (
                <div key={`${f.key}-${i}`} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-lg p-2 bg-white" data-testid={`tag-field-${f.key}`}>
                  <div className="flex gap-1">
                    <button onClick={() => moveField(i, -1)} disabled={i === 0} className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid={`tag-move-up-${f.key}`}><ArrowUp size={14} className="mx-auto"/></button>
                    <button onClick={() => moveField(i, 1)} disabled={i === layout.fields.length - 1} className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid={`tag-move-down-${f.key}`}><ArrowDown size={14} className="mx-auto"/></button>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm min-w-[86px]">
                    <input type="checkbox" checked={f.enabled} onChange={(e) => updateField(i, { enabled: e.target.checked })} data-testid={`tag-enable-${f.key}`}/>
                    <span className="font-medium">{f.key}</span>
                  </label>
                  <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} className="qw-input py-1 px-2 w-24 text-xs" placeholder="Label" data-testid={`tag-label-${f.key}`}/>
                  {f.isCustom && (
                    <input value={f.value || ""} onChange={(e) => updateField(i, { value: e.target.value })} className="qw-input py-1 px-2 flex-1 min-w-[120px] text-xs" placeholder="Text to print" data-testid={`tag-value-${f.key}`}/>
                  )}
                  <input type="number" min={6} max={24} value={f.size} onChange={(e) => updateField(i, { size: Number(e.target.value) })} className="qw-input py-1 px-2 w-14 text-xs" title="Font size (px)" data-testid={`tag-size-${f.key}`}/>
                  <button type="button" onClick={() => updateField(i, { bold: !f.bold })} className={`w-7 h-7 rounded flex items-center justify-center ${f.bold ? "bg-blue-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} title="Bold" data-testid={`tag-bold-${f.key}`}><Bold size={13}/></button>
                  <button type="button" onClick={() => updateField(i, { underline: !f.underline })} className={`w-7 h-7 rounded flex items-center justify-center ${f.underline ? "bg-blue-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} title="Underline" data-testid={`tag-underline-${f.key}`}><Underline size={13}/></button>
                  <div className="flex gap-1">
                    <AlignBtn active={f.align === "left"} onClick={() => updateField(i, { align: "left" })} tid={`tag-align-left-${f.key}`}><AlignLeft size={13}/></AlignBtn>
                    <AlignBtn active={f.align === "center"} onClick={() => updateField(i, { align: "center" })} tid={`tag-align-center-${f.key}`}><AlignCenter size={13}/></AlignBtn>
                    <AlignBtn active={f.align === "right"} onClick={() => updateField(i, { align: "right" })} tid={`tag-align-right-${f.key}`}><AlignRight size={13}/></AlignBtn>
                  </div>
                  <button type="button" onClick={() => removeField(i)} className="w-7 h-7 rounded flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 ml-auto" title="Remove field" data-testid={`tag-remove-${f.key}`}><Trash2 size={13}/></button>
                </div>
              ))}
              {layout.fields.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-300 rounded-lg" data-testid="tag-fields-empty">
                  No fields yet. Click "Reset" to restore defaults or "Add Custom Text" to build from scratch.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Preview */}
        <div className="lg:sticky lg:top-20 h-fit">
          <div className="qw-dash-card p-6 no-print">
            <div className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Poppins' }}>Live Preview</div>
            <p className="text-xs text-slate-500 mb-4">Actual size {layout.width_mm}×{layout.height_mm} mm. Reflects what your TSC printer will output.</p>
            <div className="rounded-xl bg-[#F1F5F9] border-2 border-dashed border-slate-300 p-6 flex items-center justify-center overflow-auto">
              {renderTag(layout, previewData, "preview")}
            </div>
            <div className="text-[11px] text-slate-500 mt-3">Tip: fine-tune Width, Height, and font sizes to match how your labels come out of the printer. Use "Test Print" to send one sample tag to the printer.</div>
          </div>
        </div>
      </div>

      {/* Hidden print area */}
      <div className="print-area">
        <div className="tags-print" data-testid="tag-testprint-area">
          {renderTag(layout, previewData, "print-1")}
        </div>
      </div>
    </div>
  );
};

export default TagLayoutEditor;
